import moment from 'moment';
import i18n from 'i18n';
import { DeviceBusinessHoursDay } from 'core/deviceManagement/DeviceManagement';
import { NO_VALUE } from './deviceDetail';

/** 一週的順序固定從週一排到週日，與後端的 dayOfWeek 同一套編號（isoWeekday）。 */
const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7];

const HOURS_PER_DAY = 24;

const toClock = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

/**
 * 某一天營業的小時換成時段字串，例如 `09:00 - 13:00, 16:00 - 21:00`。
 *
 * 連續的小時併成一段，結束時間寫到最後一個小時的下一個整點，所以開到 23 點那一小時
 * 會寫成 `24:00`。只在同一天裡面併，不跨到隔天：每一天各自是一列，跨日併起來反而
 * 對不回是哪一天的營業時間。
 */
function formatOpenHours(openHours: number[]): string {
  if (openHours.length === 0) {
    return i18n.t('deviceManagement.labels.closedForDay');
  }
  const hours = Array.from(new Set(openHours)).sort((a, b) => a - b);
  if (hours.length === HOURS_PER_DAY) {
    return i18n.t('deviceManagement.labels.openAllDay');
  }
  const ranges: [number, number][] = [];
  hours.forEach((hour) => {
    const last = ranges[ranges.length - 1];
    if (last && last[1] + 1 === hour) {
      last[1] = hour;
    } else {
      ranges.push([hour, hour]);
    }
  });
  return ranges
    .map(([start, end]) => `${toClock(start)} - ${toClock(end + 1)}`)
    .join(', ');
}

/**
 * 某一天的營業時間字串。
 *
 * 沒有營業時間資料（undefined）與「有資料、那天不營業」（空陣列）是兩句不同的話：
 * 前者是我們不知道，顯示成沒有值；後者是確定休息。有資料卻找不到那一天時也當成不知道，
 * 不推論成休息。
 */
export function formatBusinessHoursOfDay(
  businessHours: DeviceBusinessHoursDay[] | undefined,
  dayOfWeek: number
): string {
  const day = businessHours?.find((item) => item.dayOfWeek === dayOfWeek);
  return day ? formatOpenHours(day.openHours) : NO_VALUE;
}

/**
 * 今天的營業時間。「今天」取瀏覽器當地時間的星期，與這一頁其他用到今天的地方一致。
 */
export function formatTodayBusinessHours(
  businessHours: DeviceBusinessHoursDay[] | undefined,
  today: moment.Moment = moment()
): string {
  return formatBusinessHoursOfDay(businessHours, today.isoWeekday());
}

export type WeeklyBusinessHoursRow = {
  dayOfWeek: number;
  text: string;
};

/**
 * 一週七天各一列，寫成「星期 營業時間」。沒有營業時間資料時回 undefined，
 * 由呼叫端顯示單一個沒有值，而不是展開七列都寫沒有值。
 *
 * 星期用單字（一、二…），七列並排時比「週一」好對齊；時段設定與月曆用的是另一套完整寫法。
 */
export function buildWeeklyBusinessHours(
  businessHours: DeviceBusinessHoursDay[] | undefined
): WeeklyBusinessHoursRow[] | undefined {
  if (!businessHours) {
    return undefined;
  }
  return WEEK_DAYS.map((dayOfWeek) => ({
    dayOfWeek,
    text: `${i18n.t(
      `deviceManagement.labels.weekdayShort.${dayOfWeek}`
    )} ${formatBusinessHoursOfDay(businessHours, dayOfWeek)}`
  }));
}
